/**
 * @file index.tsx
 *
 * @fileoverview Helper functions for the job bookmark analytics events.
 */
import JobBookmarkPlacement from '~/jobiqo/components/BookmarkedJobs/enums/JobBookmarkPlacement';
import {
  DIMENSIONS,
  EventParameters,
  setEventParams,
} from '~/jobiqo/lib/analytics';
import { ANONYMOUS_USER } from '~/jobiqo/lib/auth';
import { User } from '~/jobiqo/models/user';
import { Job } from '~/jobiqo/types/types';
import { concatArrayItems } from '~/jobiqo/utils/mapper';

/**
 * Returns the dimensions for a given job bookmark.
 *
 * @param {Job} job
 *   The job to return dimensions for.
 * @param {User | null}
 *  The current user.
 * @returns {EventParameters}
 *   The dimensions object for this job bookmark.
 */
export const getJobBookmarkDimensions = (
  job: Job,
  user: User | null = null,
  variant: JobBookmarkPlacement,
): EventParameters =>
  setEventParams({
    [DIMENSIONS.entity_id]: job.id,
    [DIMENSIONS.entity_type]: 'node',
    [DIMENSIONS.entity_subtype]: 'job_per_template',
    [DIMENSIONS.entity_user_id]: job.organizationProfile?.id,
    [DIMENSIONS.entity_user_name]: job.organization,
    [DIMENSIONS.organization_name]:
      job.organizationProfile?.name ?? job.organization,
    [DIMENSIONS.entity_title]: job.title,
    [DIMENSIONS.application_type]: job.applicationWorkflow,
    // Job type source field depends on the specific project.
    [DIMENSIONS.importer_ID]: job['source'],
    [DIMENSIONS.occupations]: concatArrayItems(job.occupationalField, 'label'),
    [DIMENSIONS.regions]: concatArrayItems(job.region, 'label'),
    [DIMENSIONS.upgrades]: job.jobUpgrades?.join(' | '),
    [DIMENSIONS.current_user_role]:
      concatArrayItems(user?.roles, 'targetId') ?? ANONYMOUS_USER,
    [DIMENSIONS.current_user_id]: user?.id,
    [DIMENSIONS.event_occurrence_place]: variant,
  });
